import { Component, Input } from '@angular/core';
import { AccountUsageLimitDto } from '../../models/account-usage.model';
import { LanguageService } from '../../../../services/language/language.service';

const STALE_THRESHOLD_MS = 2 * 60 * 1000;

export type FreshnessState = 'no_snapshot' | 'stale' | 'updating' | 'fresh';

@Component({
  selector: 'app-usage-freshness-indicator',
  templateUrl: './usage-freshness-indicator.component.html',
  styleUrls: ['./usage-freshness-indicator.component.scss'],
  standalone: false
})
export class UsageFreshnessIndicatorComponent {
  @Input() lastSnapshotAt: string | null = null;
  @Input() limits: AccountUsageLimitDto[] | null = null;

  constructor(private readonly languageService: LanguageService) {}

  get state(): FreshnessState {
    if (!this.lastSnapshotAt) {
      return 'no_snapshot';
    }
    if (this.hasStaleFlag()) {
      return 'stale';
    }
    const ts = Date.parse(this.lastSnapshotAt);
    if (!Number.isFinite(ts)) {
      return 'updating';
    }
    if (Date.now() - ts > STALE_THRESHOLD_MS) {
      return 'updating';
    }
    return 'fresh';
  }

  get label(): string {
    const map: Record<FreshnessState, string> = {
      no_snapshot: 'usage.freshness.no_snapshot',
      stale: 'usage.freshness.stale',
      updating: 'usage.freshness.updating',
      fresh: 'usage.freshness.fresh'
    };
    const key = map[this.state];
    const translated = this.languageService.translate(key);
    return translated && translated !== key ? translated : this.defaultCopy(this.state);
  }

  get staleLimitsTooltip(): string {
    if (this.state !== 'stale') {
      return '';
    }
    const stale = (this.limits ?? []).filter((limit) => {
      const flags = limit?.dataQuality ?? [];
      return flags.includes('snapshot_stale');
    });
    return stale.map((limit) => limit.limitCode).join(', ');
  }

  private hasStaleFlag(): boolean {
    return (this.limits ?? []).some((limit) => limit?.dataQuality?.includes('snapshot_stale'));
  }

  private defaultCopy(state: FreshnessState): string {
    switch (state) {
      case 'no_snapshot': return 'Sin snapshot disponible';
      case 'stale': return 'Datos desactualizados';
      case 'updating': return 'Actualizando datos';
      case 'fresh': return '';
    }
  }
}
